import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../../reducers/rootReducer';
import { featureFlagsSelectors } from '../../../../reducers/featureFlagSlice';
import { useGetEntityInfoQuery } from '../../../../rtk/entityInfoApi';
import {
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  AMF_DATA_EXPLORER_PAGE_FF,
  DIAGNOSTIC_PAGE,
  EVENTS_HEATMAP_PAGE_FF,
  VECTOR_SETTINGS_VISIBILITY_FF,
} from '../../../constants';
import { UserRolesType } from '../../../../components/LoginForm/types';
import useUserRole from '../../../customHooks/useUserRole';
import { updateSearchParams } from '../../../utils';
import Icon from '../../IconSvg/Icon';
import { MenuNavigationItem } from '../../muiWrappers/LelyNavigationListItem';
import { ENTITY_MENU_OPTIONS, EntityMenuOption, MenuFlags } from '../utils';

const flagSelector =
  (flag: string) =>
  (state: Parameters<typeof featureFlagsSelectors.isFlagActive>[0]) =>
    featureFlagsSelectors.isFlagActive(state, flag);

const useEntityMenuItems = (): MenuNavigationItem[] => {
  const { t } = useTranslation('menus');
  const { pathname } = useLocation();

  // Entity routes look like /entities/:id/:type/:page
  const [, root, urlEntityId, , pageName] = pathname.split('/');
  const entityId = root === 'entities' ? urlEntityId : undefined;

  // currentData is undefined while another entity is loading, unlike data
  const { currentData: entityInfo } = useGetEntityInfoQuery(entityId ?? '', {
    skip: !entityId,
  });

  const flags: MenuFlags = {
    isEngineer: useUserRole(UserRolesType.Engineer),
    isDiagnosticPageActive: useAppSelector(flagSelector(DIAGNOSTIC_PAGE)),
    isUpdatesPageActive: useAppSelector(
      flagSelector(ADE_ONLINE_UPDATES_FEATURE_FLAG),
    ),
    isAmfDataExplorerPageActive: useAppSelector(
      flagSelector(AMF_DATA_EXPLORER_PAGE_FF),
    ),
    isHeatmapPageActive: useAppSelector(flagSelector(EVENTS_HEATMAP_PAGE_FF)),
    isVectorSettingsPageActive: useAppSelector(
      flagSelector(VECTOR_SETTINGS_VISIBILITY_FF),
    ),
  };

  if (!entityId || !entityInfo) return [];

  const options = ENTITY_MENU_OPTIONS[entityInfo.type] ?? [];

  const toMenuItem = (option: EntityMenuOption): MenuNavigationItem => {
    const isActive = pageName === option.appendixUrl.split('/')[1];
    const query = option.overviewParam ? '?fromOverview=true' : '';

    return {
      label: t(option.menuTitle),
      icon: <Icon name={option.iconClass} />,
      testId: `nav-${option.appendixUrl.replace(/\//g, '-')}`,
      to: `/entities/${entityId}/${option.appendixUrl}${query}`,
      isActive,
      onClick: () => {
        if (option.overviewParam) updateSearchParams('fromOverview', '', true);
      },
    };
  };

  return options
    .filter((option) => option.isVisible?.(flags) ?? true)
    .map(toMenuItem);
};

export default useEntityMenuItems;
